/**
 * @file Thumbnail resolution for Past collaborations post links (EPD-12059).
 *
 * A collab link's thumbnail used to come only from the EPD-11945 read-through
 * stat, which is empty whenever the link was never ingested into the legacy
 * post pipeline, and which for Instagram / TikTok is a signed CDN link that
 * stops loading once it expires. This module resolves an ordered list of
 * candidates from data the page already has, with no extra network calls:
 *
 * 1. the stat's thumbnail, unless it is a signed CDN link that has expired;
 * 2. the image of the same post among the creator's connected-account recent
 *    posts (the Social Accounts block's `posts`), matched by platform + post id;
 * 3. for YouTube, the thumbnail derived from the video id (stable, no API).
 *
 * The UI tries them in order and shows the placeholder only when none load.
 */

export type SocialPlatform = "instagram" | "tiktok" | "youtube" | "pinterest";

export interface SocialPostRef {
  platform: SocialPlatform;
  id: string;
}

/** A recent post from the creator's connected accounts. */
export interface RecentPostImage {
  link?: string | null;
  image?: string | null;
  thumbnailUrl?: string | null;
}

function parseUrl(raw: string): URL | null {
  try {
    const url = new URL(raw.trim());
    return url.protocol === "http:" || url.protocol === "https:" ? url : null;
  } catch {
    return null;
  }
}

/** Hostname without `www.` / `m.` / country subdomains, e.g. `instagram.com`. */
function baseHost(url: URL): string {
  return url.hostname.toLowerCase().split(".").slice(-2).join(".");
}

const YOUTUBE_ID = /^[\w-]{11}$/;

/**
 * Pinterest's country domains: `pinterest.de`, `pinterest.co.uk`,
 * `pinterest.com.au` (any subdomain). The suffix is pinned to those shapes so
 * hosts like `pinterest.evil.com` are not treated as Pinterest.
 */
const PINTEREST_HOST =
  /(^|\.)pinterest\.(com|[a-z]{2}|co\.[a-z]{2}|com\.[a-z]{2})$/i;

function youtubeId(url: URL): string | null {
  const host = url.hostname.toLowerCase();
  let id: string | null = null;
  if (host === "youtu.be") {
    id = url.pathname.split("/")[1] ?? null;
  } else {
    const parts = url.pathname.split("/").filter(Boolean);
    id =
      parts[0] === "watch"
        ? url.searchParams.get("v")
        : ["shorts", "embed", "live", "v"].includes(parts[0] ?? "")
          ? (parts[1] ?? null)
          : null;
  }
  return id && YOUTUBE_ID.test(id) ? id : null;
}

/**
 * Identify a post link as platform + post id, or null for anything else
 * (profile pages, short links without an id, other sites). Instagram's
 * `/p/`, `/reel/`, `/reels/` and `/tv/` share one shortcode space, so they
 * match each other.
 */
export function parseSocialPost(raw: string): SocialPostRef | null {
  const url = parseUrl(raw);
  if (!url) return null;
  const parts = url.pathname.split("/").filter(Boolean);
  const host = baseHost(url);

  if (host === "instagram.com") {
    // /p/<code>, or /<username>/p/<code> as Instagram now also emits.
    const at = parts.findIndex((p) => ["p", "reel", "reels", "tv"].includes(p));
    const code = at === -1 ? undefined : parts[at + 1];
    return code ? { platform: "instagram", id: code } : null;
  }
  if (host === "tiktok.com") {
    const at = parts.findIndex((p) => p === "video" || p === "photo");
    const id = at === -1 ? undefined : parts[at + 1];
    return id && /^\d+$/.test(id) ? { platform: "tiktok", id } : null;
  }
  if (host === "youtube.com" || host === "youtu.be") {
    const id = youtubeId(url);
    return id ? { platform: "youtube", id } : null;
  }
  if (host === "pinterest.com" || PINTEREST_HOST.test(url.hostname)) {
    const at = parts.indexOf("pin");
    // Pin URLs may carry a slug prefix: /pin/some-title--1234567890/
    const id = at === -1 ? undefined : parts[at + 1]?.split("--").pop();
    return id && /^\d+$/.test(id) ? { platform: "pinterest", id } : null;
  }
  return null;
}

function refKey(ref: SocialPostRef): string {
  return `${ref.platform}:${ref.id}`;
}

/** Thumbnail derivable from the link alone: YouTube only. */
export function derivedThumbnail(raw: string): string | null {
  const ref = parseSocialPost(raw);
  return ref?.platform === "youtube"
    ? `https://i.ytimg.com/vi/${ref.id}/hqdefault.jpg`
    : null;
}

/**
 * Whether a signed social CDN link has passed its expiry: Meta (Instagram /
 * Facebook) carries `oe=<hex unix seconds>`, TikTok `x-expires=<unix seconds>`.
 * Links without an expiry (GCS backups, ytimg, pinimg) never expire here.
 */
export function isExpiredCdnUrl(raw: string, nowMs: number): boolean {
  const url = parseUrl(raw);
  if (!url) return false;
  const oe = url.searchParams.get("oe");
  if (oe && /^[0-9a-f]+$/i.test(oe)) {
    return Number.parseInt(oe, 16) * 1000 <= nowMs;
  }
  const xExpires = url.searchParams.get("x-expires");
  if (xExpires && /^\d+$/.test(xExpires)) {
    return Number(xExpires) * 1000 <= nowMs;
  }
  return false;
}

/** Index recent posts by platform + post id, keeping the first image found. */
export function indexRecentPostImages(
  posts: readonly RecentPostImage[],
): Map<string, string> {
  const out = new Map<string, string>();
  for (const post of posts) {
    const image = post.image || post.thumbnailUrl;
    const ref = post.link ? parseSocialPost(post.link) : null;
    if (!image || !ref) continue;
    const key = refKey(ref);
    if (!out.has(key)) out.set(key, image);
  }
  return out;
}

/**
 * Ordered, de-duplicated thumbnail candidates for one collab post link. Empty
 * means no thumbnail exists, so the UI shows the placeholder.
 */
export function resolvePostThumbnails({
  url,
  statThumbnail,
  recentPostImages,
  nowMs,
}: {
  url: string;
  statThumbnail: string | null | undefined;
  recentPostImages?: ReadonlyMap<string, string>;
  nowMs: number;
}): string[] {
  const ref = parseSocialPost(url);
  const candidates = [
    statThumbnail,
    ref ? recentPostImages?.get(refKey(ref)) : undefined,
    derivedThumbnail(url),
  ];
  const out: string[] = [];
  for (const c of candidates) {
    if (c && !out.includes(c) && !isExpiredCdnUrl(c, nowMs)) out.push(c);
  }
  return out;
}
